/**
 * Day-level and itinerary-metadata editor operations (add / remove / reorder /
 * rename a day, set its date, rename the itinerary, edit its description,
 * change audience, archive-toggle).
 *
 * Like `useItineraryStopOps`, every function builds a `QueuedOp` and hands it
 * to the serial mutation queue's `enqueue` (plan D8) with the same `apply`
 * optimistic transforms and `dependsOn` scopes it had inside `ItineraryEditor`
 * (#314). The editor keeps the ARIA live-region announcements, because it owns
 * that state.
 */

import { trpc } from '../../lib/trpc';
import { findDay, nextOpId, reindex } from './itineraryOptimistic';
import type { SerializedItinerary } from './itineraryTypes';
import type { QueuedOp } from './useItineraryMutationQueue';

export type ItineraryAudience = 'private' | 'unlisted' | 'public';

interface UseItineraryPlanOpsArgs {
  itineraryId: string;
  /** The optimistic snapshot the editor is rendering. */
  itinerary: SerializedItinerary;
  enqueue: (op: QueuedOp) => void;
}

export interface ItineraryPlanOps {
  addDay: () => void;
  removeDay: (dayId: string) => void;
  reorderDay: (dayId: string, toOrdinal: number) => void;
  renameDay: (dayId: string) => void;
  setDayDate: (dayId: string) => void;
  saveTitle: (value: string) => void;
  saveDescription: (value: string) => void;
  flipAudience: (audience: ItineraryAudience) => void;
  toggleArchive: () => void;
}

export const useItineraryPlanOps = ({
  itineraryId,
  itinerary: it,
  enqueue,
}: UseItineraryPlanOpsArgs): ItineraryPlanOps => {
  // Raw tRPC mutations (the queue serializes invocation; we call mutateAsync).
  const addDayMutation = trpc.itinerary.addDay.useMutation();
  const removeDayMutation = trpc.itinerary.removeDay.useMutation();
  const reorderDayMutation = trpc.itinerary.reorderDay.useMutation();
  const updateDay = trpc.itinerary.updateDay.useMutation();
  const updateMetadata = trpc.itinerary.updateMetadata.useMutation();
  const setAudience = trpc.itinerary.setAudience.useMutation();
  const archive = trpc.itinerary.archive.useMutation();
  const unarchive = trpc.itinerary.unarchive.useMutation();

  const addDay = () => {
    enqueue({
      id: nextOpId(),
      label: 'add day',
      dependsOn: {},
      apply: (snap) =>
        reindex({
          ...snap,
          days: [
            ...snap.days,
            { _id: `tmp-${nextOpId()}`, ordinal: snap.days.length, stops: [] },
          ],
        }),
      run: () => addDayMutation.mutateAsync({ itineraryId }),
    });
  };

  const removeDay = (dayId: string) => {
    enqueue({
      id: nextOpId(),
      label: 'remove day',
      dependsOn: { dayId },
      apply: (snap) =>
        reindex({
          ...snap,
          days: snap.days.filter((d) => d._id !== dayId),
        }),
      run: () => removeDayMutation.mutateAsync({ itineraryId, dayId }),
    });
  };

  const reorderDay = (dayId: string, toOrdinal: number) => {
    enqueue({
      id: nextOpId(),
      label: 'reorder day',
      dependsOn: { dayId },
      apply: (snap) => {
        const idx = snap.days.findIndex((d) => d._id === dayId);
        if (idx === -1) return snap;
        const days = [...snap.days];
        const [day] = days.splice(idx, 1);
        days.splice(Math.min(Math.max(toOrdinal, 0), days.length), 0, day);
        return reindex({ ...snap, days });
      },
      run: () => reorderDayMutation.mutateAsync({ itineraryId, dayId, toOrdinal }),
    });
  };

  const renameDay = (dayId: string) => {
    const day = findDay(it, dayId);
    const value = window.prompt('Day title', day?.title ?? '');
    if (value === null) return;
    enqueue({
      id: nextOpId(),
      label: 'rename day',
      dependsOn: { dayId },
      apply: (snap) => ({
        ...snap,
        days: snap.days.map((d) =>
          d._id === dayId ? { ...d, title: value || undefined } : d
        ),
      }),
      run: () => updateDay.mutateAsync({ itineraryId, dayId, title: value }),
    });
  };

  const setDayDate = (dayId: string) => {
    const day = findDay(it, dayId);
    const raw = window.prompt(
      'Day date (YYYY-MM-DD) — leave blank to clear',
      day?.date ?? ''
    );
    if (raw === null) return;
    const trimmed = raw.trim();
    if (trimmed && !/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      window.alert('Use the format YYYY-MM-DD (e.g. 2026-05-23).');
      return;
    }
    const next = trimmed || null;
    enqueue({
      id: nextOpId(),
      label: 'set day date',
      dependsOn: { dayId },
      apply: (snap) => ({
        ...snap,
        days: snap.days.map((d) =>
          d._id === dayId ? { ...d, date: next ?? undefined } : d
        ),
      }),
      run: () => updateDay.mutateAsync({ itineraryId, dayId, date: next }),
    });
  };

  const saveTitle = (value: string) => {
    // Title is required (Zod min 1) — an empty draft is a cancel, not a clear.
    const trimmed = value.trim();
    if (!trimmed || trimmed === it.title) return;
    enqueue({
      id: nextOpId(),
      label: 'rename itinerary',
      dependsOn: {},
      apply: (snap) => ({ ...snap, title: trimmed }),
      run: () => updateMetadata.mutateAsync({ itineraryId, title: trimmed }),
    });
  };

  const saveDescription = (value: string) => {
    // No-op when nothing actually changed — avoids a wasted mutation when the
    // user opens the editor and closes it without typing.
    const current = it.description ?? '';
    if (current === value) return;
    enqueue({
      id: nextOpId(),
      label: 'update description',
      dependsOn: {},
      apply: (snap) => ({
        ...snap,
        description: value.length > 0 ? value : undefined,
      }),
      run: () => updateMetadata.mutateAsync({ itineraryId, description: value }),
    });
  };

  const flipAudience = (audience: ItineraryAudience) => {
    enqueue({
      id: nextOpId(),
      label: 'change audience',
      dependsOn: {},
      apply: (snap) => ({ ...snap, audience }),
      run: () => setAudience.mutateAsync({ itineraryId, audience }),
    });
  };

  const toggleArchive = () => {
    const archived = it.editorialStatus === 'archived';
    enqueue({
      id: nextOpId(),
      label: archived ? 'unarchive' : 'archive',
      dependsOn: {},
      apply: (snap) => ({
        ...snap,
        editorialStatus: archived ? 'published' : 'archived',
      }),
      run: () =>
        archived
          ? unarchive.mutateAsync({ itineraryId })
          : archive.mutateAsync({ itineraryId }),
    });
  };

  return {
    addDay,
    removeDay,
    reorderDay,
    renameDay,
    setDayDate,
    saveTitle,
    saveDescription,
    flipAudience,
    toggleArchive,
  };
};
